import { works } from "@/api/works/useWorkQuery";
import { consumeTypeColor } from "@/constants/routings";
import { useModal } from "@/features/modal/ModalStackManager";
import { OperationDetailForm } from "@/features/operations/components/form/operationDetailForm";
import { RoutingDetailForm } from "@/features/routing/components/form/RoutingDetailForm";
import { DetailHeader } from "@/features/ui/detail/DetailHeader";
import { DetailLink } from "@/features/ui/detail/DetailLink";
import { DetailLinkWrapper } from "@/features/ui/detail/DetailLinkWrapper";
import { WorkView } from "@/features/work/WorkView/WorkView";
import { useToggle } from "@/hooks/useToggle";
import styled from "@emotion/styled";
import { Badge, Flex, Table, Text, Title } from "@mantine/core";
import { ProductionPlansGet200ResponseRowsInnerWorksInnerConsumeSummary } from "@sizlcorp/sizl-api-document/dist/models";
import { IconMinus, IconPlus } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { TableContainer } from "../../components/ledgerTracking";
import { useLotTrackingActions } from "../../hook/useLotTrackingActions";
import { useLotTrackingState } from "../../hook/useLotTrackingState";
import { Tbody, Td, Th, Thead, Tr } from "./forwardSummary";
import { LotTrackingForwardWorkHistoryMaterial } from "./forwardWorkHistoryMaterial";

const Links = Object.assign(DetailHeader, {
  Link: DetailLink,
  LinkWrapper: DetailLinkWrapper,
});

export const LotTrackingForwardWorkHistoryContent = () => {
  const state = useLotTrackingState();
  const actions = useLotTrackingActions();
  const { isOpen, toggle } = useToggle();
  const { openModal } = useModal();
  const { t } = useTranslation();

  useEffect(() => {
    if (state.itemCodeValue || state.lotId) {
      actions.setProductionPlan(undefined);
    }
  }, [state.itemCodeValue, state.lotId]);

  const { data: work } = useQuery({
    ...works.find({
      query: {
        $and: [{ productionPlanId: { $eq: state.productionPlan?.productionPlanId } }],
      },
      populate: ["consumeSummary", "routingData"],
    }),
    enabled: !!state.productionPlan?.lotId,
  });

  return (
    <Container>
      <Title p={8} order={3}>
        작업지시
      </Title>
      <Text fz={12} px={8}>
        공정 단계별 투입된 원부자재 및 반제품 목록을 보여줍니다.
      </Text>
      <TableContainer>
        <Table>
          <Thead backgroundColor="var(--green-0, #EBFBEE)">
            <Tr>
              <Th flex={0.1}></Th>
              <Th>작업지시 번호</Th>
              <Th>라우팅 코드</Th>
              <Th>공정명</Th>
              <Th>공정 시퀀스</Th>
              <Th>소모시점</Th>
            </Tr>
          </Thead>
          <Tbody>
            {!state.productionPlan && (
              <Tr>
                <Td colSpan={6}>
                  <Flex justify={"center"} align={"center"}>
                    <Text px={8}>요약 탭의 품목을 선택해주세요</Text>
                  </Flex>
                </Td>
              </Tr>
            )}
            {work?.data.length === 0 ? (
              <Tr>
                <Td colSpan={4}>
                  <Flex justify={"center"} align={"center"}>
                    <Text px={8}>데이터가 없습니다</Text>
                  </Flex>
                </Td>
              </Tr>
            ) : (
              work?.data.map((works, index) => {
                return (
                  <>
                    <Tr key={index}>
                      <Td flex={0.1}>
                        <Text ta="center">
                          {!isOpen(index.toString()) ? (
                            <IconPlus
                              onClick={() => toggle(index.toString())}
                              style={{ verticalAlign: "middle", cursor: "pointer" }}
                            />
                          ) : (
                            <IconMinus
                              onClick={() => toggle(index.toString())}
                              style={{ verticalAlign: "middle", cursor: "pointer" }}
                            />
                          )}
                        </Text>
                      </Td>
                      <Td>
                        <Flex justify={"flex-end"}>
                          <Links.Link
                            onClick={() =>
                              openModal(<WorkView workId={works?.id} />, "작업지시 상세", "")
                            }
                          >
                            {works?.id}
                          </Links.Link>
                        </Flex>
                      </Td>
                      <Td>
                        <Links.Link
                          onClick={() =>
                            openModal(
                              <RoutingDetailForm routingCode={works?.routingCode} />,
                              "라우팅 상세",
                              "",
                            )
                          }
                        >
                          {works?.routingCode}
                        </Links.Link>
                      </Td>
                      <Td>
                        <Links.Link
                          onClick={() =>
                            openModal(
                              <OperationDetailForm
                                operationCode={works?.routingData?.operationCode}
                              />,
                              "공정 상세",
                              "",
                            )
                          }
                        >
                          {works?.routingData?.operation?.name}
                        </Links.Link>
                      </Td>
                      <Td>
                        <Flex justify={"flex-end"}>{works?.routingData?.seq}</Flex>
                      </Td>
                      <Td>
                        <Badge size="lg" color={consumeTypeColor[works?.routingData?.consumeType!]}>
                          {t(works?.routingData?.consumeType ?? "")}
                        </Badge>
                      </Td>
                    </Tr>
                    {isOpen(index.toString()) && (
                      <Tr>
                        <Td colSpan={6}>
                          <LotTrackingForwardWorkHistoryMaterial
                            consumeSummary={
                              work?.data[0]?.consumeSummary as
                                | ProductionPlansGet200ResponseRowsInnerWorksInnerConsumeSummary[]
                                | undefined
                            }
                          />
                        </Td>
                      </Tr>
                    )}
                  </>
                );
              })
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  max-height: 534px;
  padding: 8px 32px;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  background: var(--green-0, #ebfbee);
  overflow: auto;

  Td {
    height: auto !important;
    max-height: none !important;
    overflow: visible !important;
  }
`;
